<i18n>
{
  "fr": {
    "rules": {
      "length": "{value} caractères ou plus",
      "lowercase": "1 minuscule ou plus",
      "uppercase": "1 majuscule ou plus",
      "symbol": "1 caractère spécial ou plus",
      "number": "1 chiffre ou plus"
    }
  }
}
</i18n>

<template>
  <div class="password-input">
    <div class="password-input__input-container">
      <input
        ref="input"
        v-model="password"
        v-bind="{ id, name, type, required, autofocus, autocomplete }"
        class="label-input__input"
        :placeholder="placeholder"
        @input="isForceCheckerVisible = true"
      />
      <button
        type="button"
        :class="passwordToggleClasses"
        @mousedown="showPassword"
        @mouseup="hidePassword"
        @mouseleave="hidePassword"
        @touchstart="showPassword"
        @touchend="hidePassword"
      ></button>
    </div>

    <div
      v-if="withForceChecker"
      v-show="isForceCheckerVisible"
      class="password-input__force-checker"
    >
      <div class="password-input__score">
        <div
          :class="[
            'password-input__score-value',
            {
              'password-input__score-value--validated': score === 100,
            },
          ]"
          :style="scoreValueStyle"
        ></div>
      </div>

      <ul class="password-input__rules-list">
        <li class="password-input__rule-item">
          <span
            class="icon"
            :class="{
              'icon--check': isLongEnough,
              'icon--close': !isLongEnough,
            }"
          ></span>
          {{ $t('rules.length', { value: PASSWORD_MIN_LENGTH }) }}
        </li>
        <li
          class="password-input__rule-item password-input__rule-item--long-label"
        >
          <span
            class="icon"
            :class="{
              'icon--check': containsOneSpecialChar,
              'icon--close': !containsOneSpecialChar,
            }"
          ></span>
          {{ $t('rules.symbol') }}
        </li>
        <li class="password-input__rule-item">
          <span
            class="icon"
            :class="{
              'icon--check': containsOneLowercase,
              'icon--close': !containsOneLowercase,
            }"
          ></span>
          {{ $t('rules.lowercase') }}
        </li>
        <li class="password-input__rule-item">
          <span
            class="icon"
            :class="{
              'icon--check': containsOneUppercase,
              'icon--close': !containsOneUppercase,
            }"
          ></span>
          {{ $t('rules.uppercase') }}
        </li>
        <li class="password-input__rule-item">
          <span
            class="icon"
            :class="{
              'icon--check': containsOneDigit,
              'icon--close': !containsOneDigit,
            }"
          ></span>
          {{ $t('rules.number') }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
const TYPE_PASSWORD = 'password';
const TYPE_TEXT = 'text';

const NB_PASSWORD_RULES = 5;
const PASSWORD_MIN_LENGTH = 8;

export default {
  name: 'PasswordInput',
  props: {
    id: {
      type: String,
      default: 'password',
    },
    name: {
      type: String,
      default: 'password',
    },
    autocomplete: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    autofocus: Boolean,
    required: Boolean,
    withForceChecker: Boolean,
  },
  data() {
    return {
      PASSWORD_MIN_LENGTH,
      type: TYPE_PASSWORD,
      password: this.value || '',
      isForceCheckerVisible: false,
    };
  },
  computed: {
    /**
     * @returns {boolean}
     */
    isLongEnough() {
      return this.password.length >= PASSWORD_MIN_LENGTH;
    },
    /**
     * @returns {boolean}
     */
    containsOneLowercase() {
      return null !== this.password.match(/[a-z]/g);
    },
    /**
     * @returns {boolean}
     */
    containsOneUppercase() {
      return null !== this.password.match(/[A-Z]/g);
    },
    /**
     * @returns {boolean}
     */
    containsOneDigit() {
      return null !== this.password.match(/[0-9]/g);
    },
    /**
     * @returns {boolean}
     */
    containsOneSpecialChar() {
      return null !== this.password.match(/[^A-Za-z0-9]/g);
    },
    /**
     * @returns {number}
     */
    score() {
      return (
        ((this.isLongEnough +
          this.containsOneLowercase +
          this.containsOneUppercase +
          this.containsOneDigit +
          this.containsOneSpecialChar) *
          100) /
        NB_PASSWORD_RULES
      );
    },
    /**
     * @returns {{width: string}}
     */
    scoreValueStyle() {
      return {
        width: `${this.score}%`,
      };
    },
    /**
     * @returns {boolean}
     */
    isPasswordValid() {
      return (
        this.isLongEnough &&
        this.containsOneLowercase &&
        this.containsOneUppercase &&
        this.containsOneDigit &&
        this.containsOneSpecialChar
      );
    },
    /**
     * @returns {Array}
     */
    passwordToggleClasses() {
      return [
        'password-input__toggle',
        'icon',
        {
          'icon--eye-stroked': TYPE_PASSWORD === this.type,
          'icon--eye-2': TYPE_TEXT === this.type,
        },
      ];
    },
  },
  watch: {
    password() {
      if (!this.withForceChecker || this.isPasswordValid) {
        this.$refs.input.setCustomValidity('');
      } else {
        this.$refs.input.setCustomValidity(
          `Le mot de passe doit compter ${PASSWORD_MIN_LENGTH} caractères, au moins une majuscule, une minuscule, un chiffre et un caractère spécial.`
        );
      }
    },
  },
  methods: {
    showPassword() {
      this.type = TYPE_TEXT;
    },
    hidePassword() {
      this.type = TYPE_PASSWORD;
    },
  },
};
</script>
